// serverfeed.ts — the capture page's view of /live (ARCHITECTURE.md §7,
// §15.4), opened as soon as the scan exists rather than only after Finish.
// It always keeps the server's cloud current (SNAP replaces, DELT applies to
// the server's own last cloud, never to a phone seed) and reports it only
// while `showClouds()` says the server is the preview source — in device
// mode the phone's own seeds are the preview until Finish. server_tracking
// progress and tracking messages (server-mode capture) are summarised into
// ServerTrackingStats for the stats strip and tracking badge, and the
// tracked poses into a CameraTrail (keyframe centres + the current camera)
// the preview draws over the server's splats.
//
// Live mode (§15.7): `live` messages from the server's GPU training session
// are summarised into LiveStats. While that session is training (or handing
// off) its splats are the preview whatever showClouds() says — live mode only
// runs for a server-mode upload, so this only matters if the two ever
// disagree (e.g. a live message racing the session's switch).

import type { Scan } from '../api';
import { LiveClient } from '../live';
import type { LiveMessage, ProgressMessage, TrackingMessage } from '../live';
import { applyDelta } from '../splat/format';
import type { SplatCloud } from '../splat/format';
import { poseCameraCenter } from '../splat/math';
import type { Vec3 } from '../splat/math';

export interface ServerTrackingStats {
  frames: number;
  keyframes: number;
  lagS: number;
  state: string; // '' until the first tracking message
  poseSource: string;
  markers: number;
  quality: number;
}

// LiveStats is the latest live-mode (§15.7) state, '' phase until the first
// `live` message. Fields the server left out keep their previous value.
export interface LiveStats {
  phase: string;
  frames: number;
  keyframes: number;
  iter: number;
  gaussians: number;
  loss: number;
  psnr: number; // 0 until measured
  budgetMs: number;
  trainMs: number;
  overruns: number;
  snapshots: number;
  hz: number;
  message: string;
  modelKey: string;
}

export const emptyLiveStats = (): LiveStats => ({
  phase: '', frames: 0, keyframes: 0, iter: 0, gaussians: 0, loss: 0, psnr: 0,
  budgetMs: 0, trainMs: 0, overruns: 0, snapshots: 0, hz: 0, message: '', modelKey: '',
});

// liveStreaming: the live session is producing the splats being sent.
export const liveStreaming = (phase: string): boolean => phase === 'training' || phase === 'handoff';

// mergeLive folds one `live` message into the running stats.
export function mergeLive(s: LiveStats, m: LiveMessage): LiveStats {
  const num = (v: unknown, prev: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : prev);
  return {
    phase: m.phase,
    frames: num(m.frames, s.frames),
    keyframes: num(m.keyframes, s.keyframes),
    iter: num(m.iter, s.iter),
    gaussians: num(m.gaussians, s.gaussians),
    loss: num(m.loss, s.loss),
    psnr: num(m.psnr, s.psnr),
    budgetMs: num(m.budget_ms, s.budgetMs),
    trainMs: num(m.train_ms, s.trainMs),
    overruns: num(m.overruns, s.overruns),
    snapshots: num(m.snapshots, s.snapshots),
    hz: num(m.hz, s.hz),
    message: typeof m.message === 'string' ? m.message : s.message,
    modelKey: typeof m.model_key === 'string' ? m.model_key : s.modelKey,
  };
}

// TRAIL_MAX_KEYFRAMES caps the drawn trail (oldest dropped);
// TRAIL_EMIT_MS throttles trail updates between keyframes, so per-frame
// poses redraw the preview at most ~5 Hz.
export const TRAIL_MAX_KEYFRAMES = 400;
export const TRAIL_EMIT_MS = 200;

// TrailSnapshot is the server-tracked camera path for SplatView's `path`:
// keyframe centres in order, then the current camera (`active`).
export interface TrailSnapshot {
  centers: readonly Vec3[];
  active: number;
}

// CameraTrail collects the server tracker's camera centres: one per
// keyframe (bounded) plus the latest pose. add() says whether a fresh
// snapshot is worth publishing (a keyframe, or TRAIL_EMIT_MS since the last).
export class CameraTrail {
  private keyframes: Vec3[] = [];
  private current: Vec3 | null = null;
  private lastEmit = -Infinity;

  constructor(readonly max = TRAIL_MAX_KEYFRAMES, readonly emitMs = TRAIL_EMIT_MS) {}

  add(m: TrackingMessage, nowMs: number): boolean {
    if (!m.have_pose || !m.pose || m.pose.R?.length !== 9 || m.pose.T?.length !== 3) return false;
    const c = poseCameraCenter(m.pose.R, m.pose.T);
    if (!c.every(Number.isFinite)) return false;
    this.current = c;
    if (m.keyframe) {
      this.keyframes.push(c);
      if (this.keyframes.length > this.max) this.keyframes.splice(0, this.keyframes.length - this.max);
    }
    if (!m.keyframe && nowMs - this.lastEmit < this.emitMs) return false;
    this.lastEmit = nowMs;
    return true;
  }

  snapshot(): TrailSnapshot | null {
    if (!this.current) return null;
    const centers = [...this.keyframes, this.current];
    return { centers, active: centers.length - 1 };
  }
}

export interface ServerFeedHandlers {
  showClouds: () => boolean;
  onStatus: (scan: Scan) => void;
  onProgress: (p: ProgressMessage) => void;
  onTracking: (s: ServerTrackingStats) => void;
  onCloud: (cloud: SplatCloud) => void;
  onLive?: (s: LiveStats) => void;
  onTrail?: (t: TrailSnapshot) => void;
  now?: () => number; // trail throttle clock (tests)
}

export const emptyServerTracking = (): ServerTrackingStats => ({ frames: 0, keyframes: 0, lagS: 0, state: '', poseSource: '', markers: 0, quality: 0 });

export class ServerFeed {
  private client: LiveClient;
  private cloud: SplatCloud | null = null;
  private snaps = 0;
  readonly tracking = emptyServerTracking();
  private liveStats = emptyLiveStats();
  private trail = new CameraTrail();

  constructor(readonly scanId: string, private h: ServerFeedHandlers) {
    console.info(`[openscan/serverfeed] following scan ${scanId}`);
    this.client = new LiveClient(scanId, {
      onStatus: (scan) => h.onStatus(scan),
      onProgress: (p) => this.progress(p),
      onTracking: (t) => this.trackingMsg(t),
      onLive: (m) => this.liveMsg(m),
      onSnapshot: (cloud) => {
        this.cloud = cloud;
        this.snaps++;
        console.debug(`[openscan/serverfeed] SNAP #${this.snaps}: ${cloud.count} gaussians${this.showing() ? '' : ' (not shown: phone preview)'}`);
        this.show();
      },
      onDelta: (d) => {
        if (!this.cloud) {
          console.debug('[openscan/serverfeed] DELT before any SNAP ignored');
          return;
        }
        this.cloud = applyDelta(this.cloud, d);
        this.show();
      },
    });
  }

  get snapshots(): number {
    return this.snaps;
  }

  get live(): LiveStats {
    return { ...this.liveStats };
  }

  // showing: the server cloud is the preview source now — the session says
  // so, or a live model is streaming.
  private showing(): boolean {
    return liveStreaming(this.liveStats.phase) || this.h.showClouds();
  }

  // show reports the server cloud if it is the preview source now.
  show(): void {
    if (this.cloud && this.showing()) this.h.onCloud(this.cloud);
  }

  private liveMsg(m: LiveMessage): void {
    const prev = this.liveStats;
    const next = mergeLive(prev, m);
    this.liveStats = next;
    if (next.phase !== prev.phase) {
      console.info(`[openscan/serverfeed] live ${prev.phase || '-'} → ${next.phase}${next.message ? `: ${next.message}` : ''}${next.modelKey ? ` (model ${next.modelKey})` : ''}`);
      // A live model starting to stream makes the server cloud the preview.
      if (liveStreaming(next.phase) && !liveStreaming(prev.phase)) this.show();
    } else {
      console.debug(`[openscan/serverfeed] live ${next.phase}: ${next.keyframes} kf, ${next.gaussians} gaussians, iter ${next.iter}, train ${next.trainMs.toFixed(1)}/${next.budgetMs} ms, ${next.overruns} overruns, ${next.hz.toFixed(1)} Hz`);
    }
    this.h.onLive?.({ ...next });
  }

  private progress(p: ProgressMessage): void {
    if (p.phase === 'server_tracking') {
      const t = this.tracking;
      t.frames = p.frames ?? t.frames;
      t.keyframes = p.keyframes ?? t.keyframes;
      t.lagS = p.lag_s ?? t.lagS;
      console.debug(`[openscan/serverfeed] server_tracking frames=${t.frames} keyframes=${t.keyframes} lag=${t.lagS.toFixed(1)} s`);
      this.h.onTracking({ ...t });
    }
    this.h.onProgress(p);
  }

  private trackingMsg(m: TrackingMessage): void {
    const t = this.tracking;
    if (m.state !== t.state) console.info(`[openscan/serverfeed] server tracking state ${t.state || '-'} → ${m.state} (${m.pose_source || '-'})`);
    t.state = m.state;
    t.poseSource = m.pose_source ?? '';
    t.markers = m.markers?.length ?? 0;
    t.quality = m.quality ?? 0;
    this.h.onTracking({ ...t });
    if (this.h.onTrail && this.trail.add(m, (this.h.now ?? (() => performance.now()))())) {
      const snap = this.trail.snapshot();
      if (snap) this.h.onTrail(snap);
    }
  }

  close(): void {
    this.client.close();
  }
}
